import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { API } from "@/lib/api";
import { Logo } from "@/components/landing/Logo";
import { ToolkitCard } from "@/components/toolkit/ToolkitCard";

export default function MyToolkit() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const token = params.get("token");

  const items = Array.isArray(data?.items) ? data.items : [];
  const preparing = items.some((it) => it.status === "preparing");

  useEffect(() => {
    let alive = true;
    const load = () => axios.get(`${API}/toolkit/${id}/resources`, { params: { token } })
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setError(true); });
    load();
    return () => { alive = false; };
  }, [id, token]);

  useEffect(() => {
    if (!preparing) return undefined;
    const t = setInterval(() => {
      axios.get(`${API}/toolkit/${id}/resources`, { params: { token } }).then((r) => setData(r.data)).catch(() => {});
    }, 6000);
    return () => clearInterval(t);
  }, [preparing, id, token]);

  const ready = items.filter((it) => it.available).length;
  return (
    <div data-testid="my-toolkit-page" className="min-h-screen bg-[#F7FAFC]">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex h-[68px] max-w-[1040px] items-center px-5"><Logo /></div>
      </header>
      <main className="mx-auto max-w-[1040px] px-5 py-10">
        {error ? (
          <p data-testid="my-toolkit-error" className="rounded-xl bg-white p-8 text-center text-slate-700">This toolkit link is not valid. Please use the link from your email.</p>
        ) : (
          <>
            <h1 className="font-display text-4xl font-extrabold tracking-tight text-[#0B1B33] sm:text-5xl">{data?.name ? `${data.name}'s` : "Your"} <span className="text-[#0E9384]">AI Project Toolkit</span></h1>
            <p className="mt-3 max-w-[720px] text-base text-slate-700 md:text-lg">Seven resources to go from learning to landing. Five of them are written for your role, experience and goal, so every script, prompt and map fits you.</p>
            {data && <div className="mt-5 flex flex-wrap gap-2 text-[13px]" data-testid="toolkit-profile">
              {[data.role, data.years, `Goal: ${data.goal}`].filter(Boolean).map((x) => <span key={x} className="rounded-full border border-slate-200 bg-white px-3 py-1.5 font-medium text-slate-700">{x}</span>)}
              <span data-testid="toolkit-ready-count" className="rounded-full bg-[#0B1B33] px-3 py-1.5 font-semibold text-white">{ready} of {items.length} ready</span>
            </div>}
            {preparing && <p data-testid="toolkit-preparing-banner" className="mt-6 flex items-center gap-2 rounded-xl border border-sky-200 bg-sky-50 p-4 text-[14px] text-sky-900">
              <Loader2 className="h-4 w-4 shrink-0 animate-spin" />We're tailoring your guides right now. They appear here one by one (usually within 3-5 minutes), so you can keep this page open or come back later from your email.</p>}
            <div className="mt-8 grid gap-5">
              {items.map((it, i) => <ToolkitCard key={it.key} it={it} i={i} onDiagnostic={() => nav("/go?diagnostic=1")} />)}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
